import whatInput from 'what-input';

import {
  assertIsHTMLElement,
  getFocusableElements,
  lazySelector,
  Transition,
} from '@sg-core/resources/assets/js/utils';

const states = {
  TRANSITIONING: 'phxsg-is-transitioning',
  ACTIVE: 'phxsg-is-active',
  DISAPPEARING: 'phxsg-is-disappearing',
};

export class Drawers {
  private el: Element;
  private toggles: HTMLElement[];
  private activeDrawers: HTMLElement[] = [];
  private drawers: Map<string, HTMLElement>;
  private onClick: (e: Event) => void;

  constructor(el: string | HTMLElement) {
    this.el = lazySelector(el);

    this.toggles = Array.from(document.querySelectorAll('[data-toggle="drawer"][data-target]')) as HTMLElement[];

    const initialDrawer = this.el.querySelector('.phxsg-drawer');

    if (initialDrawer) {
      assertIsHTMLElement(initialDrawer);

      this.activeDrawers = [initialDrawer];
    }

    this.drawers = new Map<string, HTMLElement>();

    this.el.querySelectorAll('.phxsg-drawer').forEach((drawer: Element) => {
      assertIsHTMLElement(drawer);

      this.drawers.set(drawer.id, drawer);

      if (drawer !== this.activeDrawers[0]) {
        drawer.remove();
      }
    });

    this.onClick = this.handleClick.bind(this);

    this.addListeners();
  }

  private addListeners(): void {
    this.el.addEventListener('click', this.onClick);
  }

  private removeListeners(): void {
    this.el.removeEventListener('click', this.onClick);
  }

  /**
   * Handles the click event for the entire drawers element
   */
  private handleClick(e: Event): void {
    assertIsHTMLElement(e.target);

    const target = e.target.dataset.target?.replace('#', '');

    if (e.target.dataset.toggle !== 'drawer' || !target) {
      return;
    }

    e.preventDefault();

    const drawer = this.drawers.get(target);

    if (drawer) {
      this.toggle(drawer);
    }
  }

  /**
   * Sets or updates the passed in drawer toggle attributes based on the given
   * expanded value.
   */
  private updateTogglesAttributes(drawer: HTMLElement, expanded: boolean) {
    const activeDrawer = this.activeDrawers[this.activeDrawers.length - 1];
    const activeDrawerFocusables = getFocusableElements(activeDrawer);
    const toggles = this.toggles.filter((toggle) => toggle.dataset.target === `#${drawer.id}`);

    toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded.toString());

      if (activeDrawerFocusables.includes(toggle) && toggle.tabIndex === 0) {
        toggle.focus({
          preventScroll: whatInput.ask() !== 'keyboard',
        });
      }
    });
  }

  /**
   * Shows the given drawer and hides the previous one
   */
  private show(drawer: HTMLElement, immediate = false): void {
    if (immediate) {
      this.el.append(drawer);

      drawer.classList.add(states.ACTIVE);

      this.activeDrawers.forEach((activeDrawer) => {
        if (document.body.contains(activeDrawer)) {
          activeDrawer.remove();
        }
      });
      this.activeDrawers.push(drawer);

      this.updateTogglesAttributes(drawer, true);
      return;
    }

    new Transition({
      element: drawer,
      before: () => {
        this.el.append(drawer);
        drawer.classList.add(states.TRANSITIONING);

        this.activeDrawers.forEach((activeDrawer) => {
          if (document.body.contains(activeDrawer)) {
            activeDrawer.classList.add(states.DISAPPEARING);
          }
        });
      },
      transition: () => {
        setTimeout(() => {
          drawer.classList.add(states.ACTIVE);
        }, 50);
      },
      after: () => {
        drawer.classList.remove(states.TRANSITIONING);

        this.activeDrawers.forEach((activeDrawer) => {
          if (document.body.contains(activeDrawer)) {
            activeDrawer.classList.remove(states.DISAPPEARING);
            activeDrawer.remove();
          }
        });
        this.activeDrawers.push(drawer);

        this.updateTogglesAttributes(drawer, true);
      },
    });
  }

  /**
   * Hides the given drawer and shows the previous one
   */
  private hide(drawer: HTMLElement, immediate = false): void {
    this.activeDrawers = this.activeDrawers.filter((activeDrawer) => activeDrawer !== drawer);
    const previousDrawer = this.activeDrawers[this.activeDrawers.length - 1];

    if (immediate) {
      this.el.insertBefore(previousDrawer, drawer);

      drawer.classList.remove(states.ACTIVE);
      drawer.remove();

      this.updateTogglesAttributes(drawer, false);
      return;
    }

    new Transition({
      element: drawer,
      before: () => {
        this.el.insertBefore(previousDrawer, drawer);
        previousDrawer.classList.add(states.DISAPPEARING);
        previousDrawer.classList.add(states.ACTIVE);

        setTimeout(() => {
          previousDrawer.classList.remove(states.DISAPPEARING);
        }, 50);

        drawer.classList.add(states.TRANSITIONING);
      },
      transition: () => {
        setTimeout(() => {
          drawer.classList.remove(states.ACTIVE);
        }, 50);
      },
      after: () => {
        this.updateTogglesAttributes(drawer, false);

        drawer.classList.remove(states.TRANSITIONING);
        drawer.remove();
      },
    });
  }

  /*
  |-----------------------------------------------------------------------------
  | Public
  |-----------------------------------------------------------------------------
  */

  public reset(immediate = false): void {
    if (this.activeDrawers.length > 1) {
      this.activeDrawers.reverse().forEach((activeDrawer, i) => {
        if (i < this.activeDrawers.length) {
          this.hide(activeDrawer, immediate);
        }
      });
    }
  }

  /**
   * Toggles the given drawer based on its current state
   */
  public toggle(drawer: HTMLElement | string | undefined, immediate = false): void {
    let drawerElement: HTMLElement | undefined;

    if (typeof drawer === 'string') {
      drawerElement = this.drawers.get(drawer.replace('#', ''));
      // Force `immediate` to be true to avoid cases where the drawers is not
      // even displayed yet
      immediate = true;
    } else {
      drawerElement = drawer;
    }

    if (!drawerElement || !this.drawers.has(drawerElement.id)) {
      console.warn("[Styleguide: Drawers] Couldn't find the given drawer.");
      return;
    }

    if (drawerElement.classList.contains(states.ACTIVE)) {
      this.hide(drawerElement, immediate);
    } else {
      this.show(drawerElement, immediate);
    }
  }

  /**
   * Destroys the instance by removing the binded event listeners
   */
  public destroy(): void {
    this.removeListeners();
  }
}
